/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { useContext } from 'react';
import DiviFieldCard from '../../../customs/DiviFieldCard/DiviFieldCard';
import { CardId } from '../../../../models/diviCard';
import { FormControl } from '@mui/material';
import { useCSS } from '../../../../provider/CSSProvider';
import style from './TreatmentsReanimation.scss?inline';
import {
  getReanimationTuplesFor,
  REANIMATION_RECORD_ELEMENT_KEY,
  ReanimationCompressionKeys,
  ReanimationCoolingByKeys,
  ReanimationCoolingKeys,
  ReanimationDeviceKeys,
  ReanimationFeedbackKeys,
  ReanimationInstructionKeys,
  ReanimationRecord,
} from '../../../../models/genericElements/treatments/reanimation';
import CheckboxList from '../../../customs/checkboxList/CheckboxList';
import { NodeType } from '../../../../backendModels/report.model';
import { InputState } from '../../../../backendModels/general.model';
import { ReportsAPIContext } from '../../../../provider/ReportsAPIProvider';
import RadioList from '../../../customs/radioList/RadioList';
import { Draft } from 'immer';
import {
  GenericRecordMultiSelects,
  GenericRecordMultiSelectType,
  GenericRecordSingleSelects,
  GenericRecordSingleSelectType,
  GenericRecordValues,
  isGenericRecordDeletable,
} from '../../../../models/generic';
import { TimePicker } from '../../../customs/timePicker/TimePicker';

const compressionItems = getReanimationTuplesFor([ReanimationCompressionKeys.cardiacMassage]);
const feedbackItems = getReanimationTuplesFor([ReanimationFeedbackKeys.manual, ReanimationFeedbackKeys.mechanical]);
const deviceItems = getReanimationTuplesFor([
  ReanimationDeviceKeys.vvEcmo,
  ReanimationDeviceKeys.vaEcmo,
  ReanimationDeviceKeys.reboa,
]);
const coolingItems = getReanimationTuplesFor([ReanimationCoolingKeys.activeCooling]);
const coolingByItems = getReanimationTuplesFor([
  ReanimationCoolingByKeys.infusion,
  ReanimationCoolingByKeys.coolingPack,
  ReanimationCoolingByKeys.technical,
]);
const instructionItems = getReanimationTuplesFor([ReanimationInstructionKeys.phoneInstruction]);

interface ReanimationProps {
  nodeType: NodeType;
}

export default function TreatmentsReanimation({ nodeType }: ReanimationProps) {
  useCSS(style);

  const { findRecordOrDefault, adaptRecord } = useContext(ReportsAPIContext);

  const record = findRecordOrDefault('generic', nodeType, REANIMATION_RECORD_ELEMENT_KEY) as ReanimationRecord;

  const timeString =
    record.inputState == InputState.ENTERED ? (record.values.reanimationBeginnKompressionen?.text ?? '') : '';

  const updateParentCheckbox = <
    FIELD_NAME extends keyof GenericRecordMultiSelects<ReanimationRecord>,
    OPTIONS_TYPE extends GenericRecordMultiSelectType<ReanimationRecord, FIELD_NAME>,
  >(
    fieldName: FIELD_NAME,
    newValues: OPTIONS_TYPE[],
    subFieldNames: (keyof GenericRecordValues<ReanimationRecord>)[] = []
  ) => {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<ReanimationRecord>, deleteRecord) => {
        draft.inputState = InputState.ENTERED;
        if (newValues.length > 0) {
          draft.values = {
            ...draft.values,
            [fieldName]: {
              fieldType: 'multiSelect',
              options: newValues,
            },
          };
        } else {
          delete draft.values?.[fieldName];
          for (const subFieldName of subFieldNames) {
            delete draft.values?.[subFieldName];
          }

          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        }
      },
      REANIMATION_RECORD_ELEMENT_KEY
    );
  };

  const updateChildRadio = <
    FIELD_NAME extends keyof GenericRecordSingleSelects<ReanimationRecord>,
    OPTIONS_TYPE extends GenericRecordSingleSelectType<ReanimationRecord, FIELD_NAME>,
    PARENT_FIELD_NAME extends keyof GenericRecordMultiSelects<ReanimationRecord>,
    PARENT_OPTIONS_TYPE extends GenericRecordMultiSelectType<ReanimationRecord, PARENT_FIELD_NAME>,
  >(
    fieldName: FIELD_NAME,
    newValue: OPTIONS_TYPE | null,
    parentFieldName: PARENT_FIELD_NAME,
    parentOptions: PARENT_OPTIONS_TYPE[]
  ) => {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<ReanimationRecord>, deleteRecord) => {
        if (newValue != null) {
          draft.inputState = InputState.ENTERED;
          draft.values = {
            ...draft.values,
            [fieldName]: {
              fieldType: 'singleSelect',
              option: newValue,
            },
            [parentFieldName]: {
              fieldType: 'multiSelect',
              options: parentOptions,
            },
          };
        } else {
          delete draft.values?.[fieldName];
          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        }
      },
      REANIMATION_RECORD_ELEMENT_KEY
    );
  };

  const updateTime = (newTime: string) => {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<ReanimationRecord>, deleteRecord) => {
        draft.inputState = InputState.ENTERED;
        if (newTime !== '') {
          draft.values = {
            ...draft.values,
            reanimationKompressionen: {
              fieldType: 'multiSelect',
              options: [ReanimationCompressionKeys.cardiacMassage],
            },
            reanimationBeginnKompressionen: {
              fieldType: 'text',
              text: newTime,
            },
          };
        } else {
          delete draft.values?.reanimationBeginnKompressionen;
          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        }
      },
      REANIMATION_RECORD_ELEMENT_KEY
    );
  };

  return (
    <DiviFieldCard
      cardType={CardId.TreatmentsReanimation}
      subtitle='(Ergänzende Angaben im Kasten “Reanimation / Tod / Todesfeststellung”)'>
      <div className='measures-reanimation symptoms-list'>
        <FormControl fullWidth>
          <div className='row'>
            <div>
              <div className='symptoms-list'>
                <CheckboxList
                  items={compressionItems}
                  selectedValues={
                    record.inputState === InputState.ENTERED
                      ? (record.values.reanimationKompressionen?.options ?? [])
                      : []
                  }
                  onValuesChange={(newOptions) =>
                    updateParentCheckbox('reanimationKompressionen', newOptions, [
                      'reanimationFeedback',
                      'reanimationBeginnKompressionen',
                    ])
                  }
                />
                <RadioList
                  className='indented'
                  items={feedbackItems}
                  value={
                    record.inputState === InputState.ENTERED
                      ? (record.values.reanimationFeedback?.option ?? null)
                      : null
                  }
                  onChange={(value) =>
                    updateChildRadio(
                      'reanimationFeedback',
                      value as ReanimationFeedbackKeys | null,
                      'reanimationKompressionen',
                      [ReanimationCompressionKeys.cardiacMassage]
                    )
                  }
                />
              </div>
              <div className='small-spacer' />
              <TimePicker label='Beginn HDM' value={timeString} onChange={updateTime} />
            </div>
            <div className='symptoms-list'>
              <CheckboxList
                items={deviceItems}
                selectedValues={
                  record.inputState === InputState.ENTERED
                    ? (record.values.reanimationSpezialGeraet?.options ?? [])
                    : []
                }
                onValuesChange={(newOptions) => updateParentCheckbox('reanimationSpezialGeraet', newOptions)}
              />
            </div>
            <div className='symptoms-list'>
              <CheckboxList
                items={coolingItems}
                selectedValues={
                  record.inputState === InputState.ENTERED ? (record.values.reanimationKuehlung?.options ?? []) : []
                }
                onValuesChange={(newOptions) =>
                  updateParentCheckbox('reanimationKuehlung', newOptions, ['reanimationKuehlungDurch'])
                }
              />
              <RadioList
                className='indented'
                items={coolingByItems}
                value={
                  record.inputState === InputState.ENTERED
                    ? (record.values.reanimationKuehlungDurch?.option ?? null)
                    : null
                }
                onChange={(value) =>
                  updateChildRadio(
                    'reanimationKuehlungDurch',
                    value as ReanimationCoolingByKeys | null,
                    'reanimationKuehlung',
                    [ReanimationCoolingKeys.activeCooling]
                  )
                }
              />
            </div>
            <div className='symptoms-list'>
              <CheckboxList
                items={instructionItems}
                selectedValues={
                  record.inputState === InputState.ENTERED ? (record.values.reanimationTelAnleitung?.options ?? []) : []
                }
                onValuesChange={(newOptions) => updateParentCheckbox('reanimationTelAnleitung', newOptions)}
              />
            </div>
          </div>
        </FormControl>
      </div>
    </DiviFieldCard>
  );
}
